import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router";

import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import {
  CopyableValue,
  DetailsRail,
  DetailsRailSection,
} from "@/shared/components/DetailsRail";
import { FileMetadata } from "@/shared/components/FileMetadataAndTopics/FileMetadata";
import { UpdateFileTags } from "@/shared/components/FileMetadataAndTopics/UpdateFileTags";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal/RobotoPrincipalUnderlineLink.tsx";
import { FormattedDateTime } from "@/shared/datetime";
import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { FileRecord } from "@/shared/domain/files";
import {
  ancestorDirectories,
  basename,
  dirname,
} from "@/shared/domain/files/filesystem";
import { Goto } from "@/shared/navigation";
import { formatByteSize } from "@/shared/text";

import styles from "./FileDetails.module.css";

interface FileDetailsProps {
  fileRecord: FileRecord;
}

export function FileDetails({ fileRecord }: FileDetailsProps) {
  const filename = basename(fileRecord.relative_path);
  const directory = dirname(fileRecord.relative_path);
  const showRelativePath = directory && directory !== "." && directory !== "/";

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Filename</div>
          <CopyableValue text={filename} />
        </div>

        {showRelativePath && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>Relative Path</div>
            <RelativePathValue
              associationId={fileRecord.association_id}
              relativePath={fileRecord.relative_path}
            />
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Tags</div>
          <UpdateFileTags
            fileId={fileRecord.file_id}
            resourceOwnerId={fileRecord.org_id}
          />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Metadata</div>
          <FileMetadata fileId={fileRecord.file_id} />
        </div>
      </div>

      <DetailsRail className={styles.rail}>
        <DetailsRailSection label="File Size">
          {fileRecord.size !== undefined && formatByteSize(fileRecord.size)}
        </DetailsRailSection>

        <DetailsRailSection label="Created">
          {fileRecord.created !== undefined && (
            <FormattedDateTime value={fileRecord.created} />
          )}
        </DetailsRailSection>

        <DetailsRailSection label="Last Modified">
          {fileRecord.modified !== undefined && (
            <FormattedDateTime value={fileRecord.modified} />
          )}
        </DetailsRailSection>

        <DetailsRailSection label="Source">
          <RobotoPrincipalUnderlineLink principal={fileRecord.created_by} />
        </DetailsRailSection>

        <DetailsRailSection label="File ID">
          <CopyableValue
            text={fileRecord.file_id}
            eventName="CopyToClipboardClicked"
            eventProps={{ link: fileRecord.file_id }}
          />
        </DetailsRailSection>

        <DetailsRailSection
          label={
            fileAssociationType(fileRecord.association_id) ===
            AssociationType.Dataset
              ? "Dataset ID"
              : "Association ID"
          }
        >
          <CopyableValue
            text={fileRecord.association_id}
            to={Goto.createAssociationRoute(fileRecord.association_id)}
          />
        </DetailsRailSection>

        {fileRecord.device_id && (
          <DetailsRailSection label="Device ID">
            <CopyableValue
              text={fileRecord.device_id}
              to={Goto.createDeviceRoute(fileRecord.device_id)}
            />
          </DetailsRailSection>
        )}
      </DetailsRail>
    </div>
  );
}

interface RelativePathValueProps {
  associationId: string;
  relativePath: string;
}

/**
 * A file's relative path, with each parent directory linked to that directory
 * in the file browser of the dataset, org, device or device type that holds
 * the file, so a reader can jump to any level of the path.
 * The filename stays plain text: that file is already open.
 */
function RelativePathValue({
  associationId,
  relativePath,
}: RelativePathValueProps) {
  const directories = ancestorDirectories(relativePath);

  return (
    <div className={classNames(styles.sectionContent, styles.copyableContent)}>
      <span className={styles.copyableText}>
        {directories.map((directory) => (
          <React.Fragment key={directory.path}>
            <Link
              className={styles.link}
              to={Goto.createAssociationRoute(associationId, directory.path)}
            >
              {directory.name}
            </Link>
            {"/"}
          </React.Fragment>
        ))}
        {basename(relativePath)}
      </span>
      <CopyToClipboardButton text={relativePath} />
    </div>
  );
}
